(() => {
  if (customElements.get('powr-volume-ladder')) return;

  class PowrVolumeLadder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.locale || 'nl-NL';
      this.unitSingular = this.dataset.unitSingular || 'stuk';
      this.unitPlural = this.dataset.unitPlural || 'stuks';
      this.unitsPerPack = Number(this.dataset.unitsPerPack || 0);
      this.metricLabel = this.dataset.metricLabel || '/wasbeurt';
      this.washesLabel = this.dataset.washesLabel || 'wasbeurten';
      this.defaultQty = Number(this.dataset.defaultQty || 4);
      this.atcLabel = this.dataset.atcLabel || 'IN WINKELWAGEN';
      this.productId = String(this.dataset.productId || '');

      this.tiers = this.normalizeTiers(this.parseJson('[data-powr-vl-tiers-json]', []));
      this.variants = this.parseJson('[data-powr-vl-variants-json]', []);

      this.tiersEl = this.querySelector('.powr-vl__tiers');
      this.atcButton = this.querySelector('[data-powr-vl-atc]');
      this.atcLabelEl = this.querySelector('[data-powr-vl-atc-label]');
      this.statusEl = this.querySelector('[data-powr-vl-status]');

      this.buildTierButtons();
      this.tierButtons = [...this.querySelectorAll('[data-powr-vl-tier]')];

      const defaultTier =
        this.tierButtons.find((btn) => Number(btn.dataset.quantity) === this.defaultQty) ||
        this.tierButtons[0];

      this.selectedQty = Number(defaultTier?.dataset.quantity || this.defaultQty);

      this.addEventListener('click', (event) => {
        const tierButton = event.target.closest('[data-powr-vl-tier]');
        if (tierButton && this.contains(tierButton)) {
          this.selectTier(Number(tierButton.dataset.quantity));
          return;
        }

        const atc = event.target.closest('[data-powr-vl-atc]');
        if (atc && this.contains(atc)) {
          this.addToCart();
        }
      });

      document.addEventListener('variant:change', () => this.render());
      document.addEventListener('pb:variant-change', () => this.render());

      this.hideSiblingBuyButtons();
      this.render();
    }

    normalizeTiers(raw) {
      let tiers = raw;

      if (typeof tiers === 'string') {
        try {
          tiers = JSON.parse(tiers);
        } catch (error) {
          console.warn('[PowrVolumeLadder] Invalid tiers JSON string', error);
          return [];
        }
      }

      if (!Array.isArray(tiers)) return [];

      return tiers
        .map((tier) => ({
          quantity: Number(tier?.quantity ?? tier?.qty ?? 0),
          total: tier?.total ?? tier?.bundle_total ?? null,
          total_cents: tier?.total_cents ?? null,
          label: typeof tier?.label === 'string' ? tier.label.trim() : '',
        }))
        .filter((tier) => tier.quantity > 0)
        .sort((a, b) => b.quantity - a.quantity);
    }

    buildTierButtons() {
      if (!this.tiersEl) return;

      this.tiersEl.replaceChildren();

      for (const tier of this.tiers) {
        const qty = Number(tier.quantity);
        const selected = qty === Number(this.defaultQty);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `powr-vl__tier${selected ? ' is-selected' : ''}`;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.dataset.powrVlTier = '';
        button.dataset.quantity = String(qty);
        button.innerHTML =
          '<span class="powr-vl__badge" data-powr-vl-badge hidden></span>' +
          `<span class="powr-vl__qty" data-powr-vl-label>${qty} ${this.unitNoun(qty)}</span>` +
          '<span class="powr-vl__prices">' +
          '<s class="powr-vl__compare" data-powr-vl-compare hidden></s>' +
          '<strong class="powr-vl__total" data-powr-vl-total></strong>' +
          '</span>';
        this.tiersEl.appendChild(button);
      }
    }

    parseJson(selector, fallback) {
      const node = this.querySelector(selector);
      if (!node) return fallback;

      try {
        return JSON.parse(node.textContent || 'null') || fallback;
      } catch (error) {
        console.warn('[PowrVolumeLadder] Failed to parse JSON', selector, error);
        return fallback;
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(amount);
      } catch (error) {
        return `€${amount.toFixed(2).replace('.', ',')}`;
      }
    }

    unitNoun(qty) {
      return Number(qty) === 1 ? this.unitSingular : this.unitPlural;
    }

    selectedTier() {
      return this.tiers.find((tier) => Number(tier.quantity) === Number(this.selectedQty)) || this.tiers[0] || null;
    }

    currentVariant() {
      const scope =
        this.closest('.product-information') ||
        this.closest('.product-details') ||
        document;

      const checked = scope.querySelector(
        `variant-picker[data-product-id="${this.productId}"] input[name^="options"]:checked[data-variant-id]`
      );
      if (checked?.dataset.variantId) {
        const fromList = this.variants.find((variant) => String(variant.id) === String(checked.dataset.variantId));
        if (fromList) return fromList;
        return { id: checked.dataset.variantId, price: Number(checked.dataset.price || 0) };
      }

      const formInput = scope.querySelector(
        `product-form-component[data-product-id="${this.productId}"] input[name="id"], form[action^="/cart/add"] input[name="id"]`
      );
      if (formInput?.value) {
        return this.variants.find((variant) => String(variant.id) === String(formInput.value)) || this.variants[0] || null;
      }

      return this.variants.find((variant) => variant.available) || this.variants[0] || null;
    }

    tierTotalCents(tier) {
      if (!tier) return 0;
      if (tier.total_cents != null && tier.total_cents !== '') return Math.round(Number(tier.total_cents));
      if (tier.total != null && tier.total !== '') return Math.round(Number(tier.total) * 100);
      return 0;
    }

    selectTier(qty) {
      if (!qty || Number(qty) === Number(this.selectedQty)) return;
      this.selectedQty = Number(qty);
      this.render();
    }

    hideSiblingBuyButtons() {
      const details = this.closest('.product-details') || this.closest('.product-information');
      if (!details) return;

      for (const block of details.querySelectorAll('.buy-buttons-block')) {
        block.hidden = true;
        block.setAttribute('data-powr-vl-hidden', 'true');
      }
    }

    syncQuantityInputs() {
      const scope =
        this.closest('.product-information') ||
        this.closest('.product-details') ||
        document;
      const qty = String(this.selectedQty);

      for (const input of scope.querySelectorAll('input[name="quantity"], quantity-selector-component input, .quantity__input')) {
        input.value = qty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    updateSubtitle(qty) {
      const washes = this.unitsPerPack > 0 ? Number(qty) * this.unitsPerPack : 0;
      if (washes <= 0) return;

      const nextSuffix = `${washes} ${this.washesLabel}`;

      for (const el of document.querySelectorAll('.pb-rp-text, .mobiletitlesubtitle')) {
        const labelEl = el.querySelector('.pb-rp-text__label');
        const washesEl = el.querySelector('.pb-rp-text__washes');

        if (labelEl && washesEl) {
          washesEl.textContent = nextSuffix;
          continue;
        }

        const current = (el.textContent || '').trim();
        const base = current.split('|')[0].trim() || current;
        el.innerHTML =
          `<span class="pb-rp-text__label">${base}</span>` +
          `<span class="pb-rp-text__sep"> | </span>` +
          `<span class="pb-rp-text__washes">${nextSuffix}</span>`;
      }
    }

    render() {
      const tier = this.selectedTier();
      const variant = this.currentVariant();
      const qty = Number(this.selectedQty);
      const unitPrice = Number(variant?.price || 0);
      const compareAt = unitPrice * qty;
      const total = this.tierTotalCents(tier);
      const metricDivisor = this.unitsPerPack > 0 ? qty * this.unitsPerPack : 0;
      const perMetric = metricDivisor > 0 ? Math.round(total / metricDivisor) : 0;

      for (const button of this.tierButtons) {
        const buttonQty = Number(button.dataset.quantity);
        const buttonTier = this.tiers.find((entry) => Number(entry.quantity) === buttonQty);
        const buttonTotal = this.tierTotalCents(buttonTier);
        const buttonCompare = unitPrice * buttonQty;
        const buttonSavings = Math.max(0, buttonCompare - buttonTotal);
        const buttonPercent =
          buttonCompare > 0 && buttonSavings > 0 ? Math.round((buttonSavings / buttonCompare) * 100) : 0;
        const selected = buttonQty === qty;

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');

        const labelEl = button.querySelector('[data-powr-vl-label]');
        const compareEl = button.querySelector('[data-powr-vl-compare]');
        const totalEl = button.querySelector('[data-powr-vl-total]');
        const badgeEl = button.querySelector('[data-powr-vl-badge]');

        if (labelEl) labelEl.textContent = buttonTier?.label || `${buttonQty} ${this.unitNoun(buttonQty)}`;
        if (compareEl) {
          if (buttonCompare > buttonTotal && buttonTotal > 0) {
            compareEl.hidden = false;
            compareEl.textContent = this.formatMoney(buttonCompare);
          } else {
            compareEl.hidden = true;
            compareEl.textContent = '';
          }
        }
        if (totalEl) totalEl.textContent = this.formatMoney(buttonTotal || buttonCompare);
        if (badgeEl) {
          if (buttonPercent > 0) {
            badgeEl.hidden = false;
            badgeEl.textContent = `-${buttonPercent}%`;
          } else {
            badgeEl.hidden = true;
            badgeEl.textContent = '';
          }
        }
      }

      this.syncQuantityInputs();
      this.updateHeaderPrice(total > 0 ? total : compareAt, perMetric);
      this.updateSubtitle(qty);

      if (this.atcButton) {
        const available = variant?.available !== false && Boolean(variant?.id);
        this.atcButton.disabled = !available;
      }
    }

    updateHeaderPrice(totalCents, perMetricCents) {
      const priceHtml = `<span class="price">${this.formatMoney(totalCents)}</span>`;

      // Desktop header uses #pdp-price; mobile header uses .mobileblock .secondblock (no id).
      for (const priceRoot of document.querySelectorAll('#pdp-price, .mobileblock .secondblock')) {
        priceRoot.innerHTML = priceHtml;
      }

      const dosageNodes = document.querySelectorAll('.dosage-price');
      for (const dosage of dosageNodes) {
        if (perMetricCents > 0) {
          dosage.textContent = `${this.formatMoney(perMetricCents)}${this.metricLabel}`;
        }
      }
    }

    async addToCart() {
      const variant = this.currentVariant();
      const qty = Number(this.selectedQty);
      if (!variant?.id || qty < 1) return;

      if (this.atcButton) this.atcButton.disabled = true;
      if (this.atcLabelEl) this.atcLabelEl.textContent = '...';
      if (this.statusEl) this.statusEl.textContent = '';

      try {
        if (window.PBCart?.addLine) {
          await window.PBCart.addLine(variant.id, qty);
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: Number(variant.id), quantity: qty }),
          });
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
      } catch (error) {
        console.warn('[PowrVolumeLadder] Add to cart failed', error);
        if (this.statusEl) this.statusEl.textContent = 'Kon niet toevoegen. Probeer opnieuw.';
      } finally {
        if (this.atcLabelEl) this.atcLabelEl.textContent = this.atcLabel;
        this.render();
      }
    }
  }

  customElements.define('powr-volume-ladder', PowrVolumeLadder);
})();
